/**
 * SHARE — the dialog behind the ACTIVE SONG panel's Share button.
 *
 * One job: take a Studio song from "not shareable yet" to "here is the link, get it
 * somewhere useful" — and make the boundary visible.
 *
 * The user asked for an explicit discoverability step before a link appears at all, so
 * this dialog now has two states:
 *
 *   Not discoverable   no URL, no Share/Copy actions. The only route forward is making the
 *                      song discoverable on Lyrixa.
 *   Discoverable       the Lyrixa link is shown in full and can be shared through the system
 *                      sheet or copied directly.
 *
 * The rule is the app's existing one: "discoverable" means `visibility === 'public'`.
 * `unlisted` remains a share-API concept, not a Studio control. See hooks/useSongs.ts and
 * lib/discovery.ts.
 *
 * TWO ROUTES OUT once a song is discoverable, and which one is offered depends on the browser:
 *
 *   Share…      the platform's own share sheet, via navigator.share. Offered only when the
 *               browser actually has one.
 *   Copy link   always present, even where the share sheet exists, because copying is not only
 *               the fallback — it is what somebody pasting into a message or document wants.
 *
 * The clipboard write can fail, so the result is reflected in the button and the field stays
 * selectable as the manual fallback. See lib/webShare.ts.
 */
import { useEffect, useState } from 'react';
import { Check, Copy, Link2, Share2 } from 'lucide-react';
import { useSongs } from '../../hooks';
import { songShareUrl } from '../../lib/share';
import { canSystemShare, copyText, shareLink } from '../../lib/webShare';
import type { ShareOutcome } from '../../lib/webShare';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';
import { Btn, Modal } from '../ui';

export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  /** The Studio song this dialog is about. */
  song: Song;
}

/** Result of the last attempt to get the link out. Null before any attempt. */
type Result = ShareOutcome | null;

export default function ShareDialog({ open, onClose, song }: ShareDialogProps) {
  const { makeDiscoverable } = useSongs();
  const [result, setResult] = useState<Result>(null);
  const [makingDiscoverable, setMakingDiscoverable] = useState(false);

  const ready = song.status === 'ready';
  const discoverable = ready && song.visibility === 'public';
  const url = discoverable ? songShareUrl(song.id) : '';

  /*
    Read at render time rather than once at module load, because the capability check is cheap
    and a stale answer is not: the dialog should offer the sheet only where it actually exists.
  */
  const systemShare = discoverable ? canSystemShare({ url }) : false;

  // Reset when the dialog closes, so reopening it does not show stale copy state or a stale
  // in-flight publish label.
  useEffect(() => {
    if (!open) {
      setResult(null);
      setMakingDiscoverable(false);
    }
  }, [open]);

  // A defensive backstop, not the primary path: `publish` below already clears the pending
  // label itself once `makeDiscoverable` settles, success or failure. This only matters if
  // `song.visibility` becomes public some other way while the dialog is open.
  useEffect(() => {
    if (discoverable) setMakingDiscoverable(false);
  }, [discoverable]);

  /** The system share sheet, falling back to the clipboard if it is refused. */
  async function share() {
    setResult(
      await shareLink({
        url,
        title: song.title,
        text: `${song.title} — made with Lyrixsa AI`,
      })
    );
  }

  /** The clipboard, directly. Always available as a route once the song is shareable. */
  async function copy() {
    setResult((await copyText(url)) ? 'copied' : 'failed');
  }

  /**
   * Makes the song discoverable, which is the prerequisite for sharing it at all.
   *
   * AWAITED, not fired-and-forgotten: `makeDiscoverable` now rolls `visibility` back to
   * `'private'` if the server rejects the PATCH (an expired session, a lapsed purchase, ...),
   * and its own toast already says so. The pending label is cleared here regardless of the
   * outcome — on success `discoverable` flips true and the effect above would eventually
   * clear it too, but on failure `discoverable` never becomes true, so this is the only
   * place that stops the button from reading "Making discoverable…" forever.
   */
  async function publish() {
    if (!ready || discoverable || makingDiscoverable) return;
    setMakingDiscoverable(true);
    await makeDiscoverable(song);
    setMakingDiscoverable(false);
  }

  return (
    <Modal open={open} onClose={onClose} title={`Share ${song.title}`} width={440}>
      {discoverable ? (
        <>
          <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 14px', lineHeight: 1.55 }}>
            Anyone with this link can open this track on Lyrixsa. Logged-in listeners land on Home,
            where the track opens in the lyrics view; your prompt and version history stay
            private.
          </p>

          {/* ---- the link ----
              A read-only input rather than a <p>: it is selectable, keyboard-copyable and
              reachable by a screen reader as a value. */}
          <label htmlFor="lyx-share-url" className="lyx-visually-hidden">
            Lyrixsa share link
          </label>
          <div className="lyx-share-row">
            <span className="lyx-share-field" style={{ background: c.surface2, borderColor: c.border }}>
              <Link2 size={13} color={c.text2} style={{ flexShrink: 0 }} aria-hidden="true" />
              <input
                id="lyx-share-url"
                readOnly
                value={url}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'none',
                  border: 'none',
                  color: c.text1,
                  fontFamily: 'IBM Plex Mono',
                  fontSize: 12,
                  padding: 0,
                }}
              />
            </span>

            {systemShare && (
              <Btn
                variant="primary"
                onClick={() => void share()}
                aria-label={`Share ${song.title} using your device`}
              >
                <Share2 size={13} />
                Share…
              </Btn>
            )}

            <Btn onClick={() => void copy()} aria-label={`Copy share link for ${song.title}`}>
              {result === 'copied' ? <Check size={13} /> : <Copy size={13} />}
              {result === 'copied' ? 'Copied' : 'Copy link'}
            </Btn>
          </div>

          <p
            role="status"
            aria-live="polite"
            style={{
              fontSize: 11,
              margin: '8px 0 0',
              minHeight: 15,
              color: result === 'failed' ? c.danger : c.text2,
            }}
          >
            {result === 'copied'
              ? 'Link copied to your clipboard.'
              : result === 'failed'
                ? 'Could not reach the clipboard — select the link above and copy it manually.'
                : ''}
          </p>
        </>
      ) : (
        <>
          <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 8px', lineHeight: 1.55 }}>
            Only discoverable tracks can be shared. Make this song discoverable first, and the
            Lyrixsa link will appear here.
          </p>
          <p style={{ fontSize: 11.5, color: c.text2, margin: '0 0 14px', lineHeight: 1.55 }}>
            Making a song discoverable adds it to the Lyrixsa Catalogue so other listeners can find
            it there too.
          </p>
          <Btn
            full
            variant="primary"
            onClick={publish}
            disabled={!ready || makingDiscoverable}
            aria-label={`Make ${song.title} discoverable`}
          >
            {makingDiscoverable ? 'Making discoverable…' : 'Make discoverable'}
          </Btn>
          {/*
            Only mounted when there is actually something to say. A song's `ready` status
            does not flip while this dialog is open, so there is no live update for a
            permanently-reserved blank line to protect against — and reserving one anyway
            was the bug: with the Done button gone, this was the last thing in the dialog,
            so its 8px margin plus its 15px of always-present (usually blank) height sat
            directly against the box's own 22px bottom padding, making the space below the
            button nearly three times the 22px the box leaves above the title. Not
            rendering it at all in the common `ready` case makes the two match.
          */}
          {!ready && (
            <p role="status" aria-live="polite" style={{ fontSize: 11, margin: '8px 0 0', color: c.text2 }}>
              Only ready tracks can be made discoverable and shared.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
